// A slim frame — the border sits right at the card edge and the corner marks
// are inset, so almost nothing needs to bleed outside. Just enough room for a
// faint outer glow line.
export const BLEED = 6;

const GOLD = '#e6c66a';

// Small diamond glyph centred at (cx,cy), half-width s.
function diamondPath(cx, cy, s) {
  return `M ${cx} ${cy - s} L ${cx + s} ${cy} L ${cx} ${cy + s} L ${cx - s} ${cy} Z`;
}

// A delicate L-shaped corner mark (like a crop/register mark) with a tiny gold
// diamond at its vertex. `dx`/`dy` (+1/-1) point the two arms inward from the
// corner along the card's edges.
function CornerMark({ cx, cy, dx, dy, len, gold }) {
  return (
    <g>
      <path
        d={`M ${cx} ${cy + dy * len} L ${cx} ${cy} L ${cx + dx * len} ${cy}`}
        fill="none" stroke={gold} strokeWidth={1} strokeLinecap="round" opacity={0.75}
      />
      <path d={diamondPath(cx, cy, 1.4)} fill={gold} opacity={0.95} />
    </g>
  );
}

// Gold Royale — a slim double-line gold border with four delicate corner marks.
// Deliberately restrained (no thick picture-frame artwork): the fine gold line
// + jewel-like corner points read as "royale" without adding visual weight.
export default function GoldOrnateFrame({ w, h, radius = 12, accent }) {
  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;
  const gold = accent || GOLD;
  const inset = 10;
  const armLen = 9;

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H}>
        {/* Faint outer glow line, just outside the card edge */}
        <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4} rx={r + 2} ry={r + 2} fill="none" stroke={gold} strokeWidth={0.6} opacity={0.22} />

        {/* Crisp gold border right at the edge */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={gold} strokeWidth={1.1} opacity={0.85} />

        {/* Faint companion line just inside — the "double border" that reads as refined rather than heavy */}
        <rect x={x + 3.5} y={y + 3.5} width={w - 7} height={h - 7} rx={Math.max(2, r - 3.5)} ry={Math.max(2, r - 3.5)} fill="none" stroke={gold} strokeWidth={0.5} opacity={0.35} />

        {/* Four delicate corner marks, inset from the edges */}
        <CornerMark cx={x + inset} cy={y + inset} dx={1} dy={1} len={armLen} gold={gold} />
        <CornerMark cx={x + w - inset} cy={y + inset} dx={-1} dy={1} len={armLen} gold={gold} />
        <CornerMark cx={x + inset} cy={y + h - inset} dx={1} dy={-1} len={armLen} gold={gold} />
        <CornerMark cx={x + w - inset} cy={y + h - inset} dx={-1} dy={-1} len={armLen} gold={gold} />
      </svg>
    </div>
  );
}
